import React, { useEffect, useMemo, useState } from 'react';
import FilterReport from '../../../components/FilterReport';
import styled from 'styled-components';
import { Badge, Divider, Drawer, Grid, Spin, Tooltip } from 'antd';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';
import useReportLog from '../../../hooks/useReportLog';
import InfoDataCompletion from '../../../components/InfoDataCompletion';
import { EAssetType } from '../../asset/types/asset.type';
import ChartRpmFuelSpeedRunning from '../components/charts/chartRpmFuelSpeedRunning';
import ChartAERunningTime from '../components/charts/chartAERunningTime';
import ChartFuelCons from '../components/charts/chartFuelCons';
import PopupDownload from '../components/downloadConfiguration';
import TableReportLog from '../components/tableReportLog';
import SummaryTable from '../components/summaryTable';
import VesselTracking from '../components/vesselTracking';
import useMasterData from '../../../hooks/useMasterData';
import TableReportOperationMode from '../components/tableReportOperationMode';
import ReportTabs, { TabItem } from '../components/reportTabs';
import { validateDeviceAvailability } from '../components/helperFunction';
import TabEngineEvents from '../components/charts/tabEngineEvents';
import TabClutchEvents from '../components/charts/tabClutchEvents';
import TabPanelDoor from '../components/charts/tabPanelDoor';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RiEqualizer2Line } from '@remixicon/react';

dayjs.extend(utc);

const ReportLog = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { asset, filter, company, timezone } = useGroup();
  // the tabs follow the asset of the loaded logs, not the one being picked
  const { asset: appliedAsset } = useAppliedReportFilter();
  const { loadingLog: loading, logs } = useReportLog();
  const { companies, isOperationMode, loadingOperationMode } = useMasterData();
  const [init, setInit] = useState<boolean>(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<number>(1);

  const isOperationModeEnabled =
    appliedAsset.metadata?.showOperationMode === 1 || isOperationMode;
  const isEventEngineEnabled = appliedAsset.metadata?.showEventEngine === 1;
  const hasPanelDoor = (appliedAsset.devices || []).some(
    (device) => device.deviceType?.name?.trim().toLowerCase() === 'panel door',
  );
  const hasOperationModeLogData = Boolean(
    logs?.operation_mode && Object.keys(logs.operation_mode).length,
  );
  const hasOperationModeData =
    isOperationMode ||
    hasOperationModeLogData ||
    loading ||
    loadingOperationMode === true;

  const tabItems = useMemo(() => {
    let tabs: TabItem[] = [];

    if (logs && Object.keys(logs).length) {
      const assetType = appliedAsset.assetType;

      if ([EAssetType.FMS, EAssetType.EMS].includes(assetType)) {
        const deviceValidation = validateDeviceAvailability(
          appliedAsset.devices || [],
          assetType
        );

        tabs = [
          {
            key: 1,
            label: 'Summary',
          },
          {
            key: 2,
            label: 'Vessel Tracking',
          },
          {
            key: 3,
            label: 'Data Log',
          },
          {
            key: 3.1,
            label: 'Operation Mode',
            disabled: !isOperationModeEnabled,
          },
          {
            key: 3.2,
            label: 'Event Engine',
            disabled: !deviceValidation.hasAnyRPM || !isEventEngineEnabled,
          },
          {
            key: 3.3,
            label: 'Event Clutch',
            disabled: !deviceValidation.hasAnyClutch,
          },
          {
            key: 3.4,
            label: 'Door Panel',
            disabled: !hasPanelDoor,
          },
          {
            key: 4,
            label:
              appliedAsset.assetType === EAssetType.EMS
                ? 'RPM Vs Speed Vs AE Running Time'
                : 'RPM Vs Fuel Cons. Vs Speed Vs AE Running Time',
            disabled: !deviceValidation.hasAnyRPM,
          },
          {
            key: 5,
            label: 'ME Fuel Cons.',
            disabled:
              appliedAsset.assetType === EAssetType.EMS ||
              !deviceValidation.hasAnyFM ||
              !deviceValidation.hasAnyRPM,
          },
          {
            key: 6,
            label: 'Port Fuel Cons. Vs Running Time',
            disabled:
              appliedAsset.assetType === EAssetType.EMS ||
              !deviceValidation.hasFMPort ||
              !deviceValidation.hasRPMPort,
          },
          {
            key: 7,
            label: 'Starboard Fuel Cons. Vs Running Time',
            disabled:
              appliedAsset.assetType === EAssetType.EMS ||
              !deviceValidation.hasFMStarboard ||
              !deviceValidation.hasRPMStarboard,
          },
          {
            key: 9,
            label: 'Center Fuel Cons. Vs Running Time',
            disabled:
              appliedAsset.assetType === EAssetType.EMS ||
              !deviceValidation.hasFMCenter ||
              !deviceValidation.hasRPMCenter,
          },
          {
            key: 8,
            label: 'AE Fuel Cons',
          },
        ].filter((item) => !item.disabled);
      }
    }

    return tabs;
  }, [logs, appliedAsset, isOperationModeEnabled, isEventEngineEnabled]);

  const _renderTabContent = () => {
    const renderMobileChartPanel = (content: React.ReactNode) =>
      isMobile ? <MobileChartPanel>{content}</MobileChartPanel> : content;

    if (activeTab === 1) {
      return <SummaryTable />;
    } else if (activeTab === 2) {
      return <VesselTracking />;
    } else if (activeTab === 3) {
      return <TableReportLog />;
    } else if (activeTab === 3.1) {
      return <TableReportOperationMode hasOperationModeData={hasOperationModeData} />;
    } else if (activeTab === 3.2) {
      return <TabEngineEvents />;
    } else if (activeTab === 3.3) {
      return <TabClutchEvents />;
    } else if (activeTab === 3.4) {
      return <TabPanelDoor />;
    } else if (activeTab === 4) {
      return renderMobileChartPanel(
        <ChartRpmFuelSpeedRunning
          isShowTitle={isMobile}
          compactControls={isMobile}
          height={isMobile ? 300 : undefined}
        />,
      );
    } else if (activeTab === 5) {
      return renderMobileChartPanel(
        <ChartFuelCons
          chartType='me'
          compactControls={isMobile}
          height={isMobile ? 300 : undefined}
          key='me'
        />,
      );
    } else if (activeTab === 6) {
      return renderMobileChartPanel(
        <ChartFuelCons
          chartType='port'
          compactControls={isMobile}
          height={isMobile ? 300 : undefined}
          key='port'
        />,
      );
    } else if (activeTab === 7) {
      return renderMobileChartPanel(
        <ChartFuelCons
          chartType='starboard'
          compactControls={isMobile}
          height={isMobile ? 300 : undefined}
          key='starboard'
        />,
      );
    } else if (activeTab === 8) {
      return renderMobileChartPanel(
        <ChartAERunningTime
          compact={isMobile}
          height={isMobile ? 300 : undefined}
        />,
      );
    } else if (activeTab === 9) {
      return renderMobileChartPanel(
        <ChartFuelCons
          chartType='center'
          compactControls={isMobile}
          height={isMobile ? 300 : undefined}
          key='center'
        />,
      );
    } else {
      return <div>Something wrong</div>;
    }
  };

  const _renderDownloadButton = () => {
    return (
      <Tooltip title='Download report data as PDF'>
        <PopupDownload loading={loading} />
      </Tooltip>
    );
  };

  useEffect(() => {
    let ignore = false;
    if (!ignore && filter.assetId) {
      setInit(true);
    }

    return () => {
      ignore = true;
    };
  }, [filter.assetId]);

  const appliedFilterCount = [
    company.companyId,
    filter.assetId,
    filter.interval,
    filter.startAt && filter.endAt,
  ].filter(Boolean).length;

  const filterDateLabel = `${dayjs(filter.startAt * 1000)
    .utcOffset(timezone * 60)
    .format('DD/MM/YY, HH:mm')} - ${dayjs(filter.endAt * 1000)
    .utcOffset(timezone * 60)
    .format('DD/MM/YY, HH:mm')}`;
  const selectedCompanyImageLink = useMemo(
    () =>
      companies.find((item) => item.companyId === company.companyId)?.imageLink,
    [companies, company.companyId],
  );

  const companyLogoSources = useMemo(
    () =>
      Array.from(
        new Set(
          [
            selectedCompanyImageLink,
            company.imageLink,
            asset.company?.imageLink,
          ].filter(
            (imageLink): imageLink is string => Boolean(imageLink),
          ),
        ),
      ),
    [asset.company?.imageLink, company.imageLink, selectedCompanyImageLink],
  );

  return (
    <ReportContainer>
      {isMobile ? (
        <MobileReportFilterHeader>
          <MobileFilterSummary>
            <MobileFilterIcon aria-hidden='true'>
              <MobileCompanyLogo sources={companyLogoSources} />
            </MobileFilterIcon>
            <MobileFilterCopy>
              <MobileFilterTitle>
                {company.name || asset.company?.name || 'Select company'}
              </MobileFilterTitle>
              <MobileFilterAssetName>
                {asset.name || 'Select vessel'}
              </MobileFilterAssetName>
              <MobileFilterRange>{filterDateLabel}</MobileFilterRange>
            </MobileFilterCopy>
            <Badge count={appliedFilterCount} size='small'>
              <MobileFilterButton
                type='button'
                aria-label='Open report filters'
                onClick={() => setFilterDrawerOpen(true)}
              >
                <RiEqualizer2Line size={20} />
              </MobileFilterButton>
            </Badge>
          </MobileFilterSummary>
        </MobileReportFilterHeader>
      ) : (
        <>
          <InfoDataCompletion />
          <Divider style={{ marginTop: 0, marginBottom: 5 }} />
          <FilterReport
            init={init}
            downloadButton={_renderDownloadButton()}
            resetDateOnInit
            minuteOnly={activeTab === 3.4}
          />
        </>
      )}
      {isMobile && (
        <FilterBottomSheetDrawer
          title='Filter'
          placement='bottom'
          height='auto'
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          forceRender
          bodyStyle={{ overflowY: 'auto', padding: 0 }}
        >
          <MobileFilterForm>
            <FilterReport
              init={init}
              downloadButton={_renderDownloadButton()}
              onApply={() => setFilterDrawerOpen(false)}
              resetDateOnInit
              variant='bottomSheet'
              minuteOnly={activeTab === 3.4}
            />
          </MobileFilterForm>
        </FilterBottomSheetDrawer>
      )}
      <Spin spinning={loading} tip="Loading data report...">
        <div style={{ minHeight: 240 }}>
          <ReportTabs
            tabItems={tabItems}
            activeTab={activeTab}
            onClickTab={(key: any) => setActiveTab(key)}
          />
          {_renderTabContent()}
        </div>
      </Spin>
    </ReportContainer>
  );
};

const MobileCompanyLogo: React.FC<{ sources: string[] }> = ({ sources }) => {
  const [sourceIndex, setSourceIndex] = useState(0);
  const src = sources[sourceIndex];

  useEffect(() => {
    setSourceIndex(0);
  }, [sources]);

  if (!src) {
    return <MobileCompanyLogoImage alt='' src='/images/flag-not-found.png' />;
  }

  return (
    <MobileCompanyLogoImage
      alt=''
      src={src}
      onError={() => setSourceIndex((index) => index + 1)}
    />
  );
};

export const ReportContainer = styled.div`
  /* background-color: var(--bg-gray); */
  padding: 15px 0;
  height: '100%';
  display: flex;
  flex-direction: column;
  gap: 5px;

  @media (max-width: 767px) {
    background: #f4f6f9;
    margin: 0 -16px;
    padding: 0 0 20px;
    gap: 0;

    > .ant-divider {
      display: none;
    }

    .ant-spin-nested-loading,
    .ant-spin-container {
      min-width: 0;
    }
  }
`;

const MobileReportFilterHeader = styled.div`
  padding: 20px 16px 12px;
  background: #ffffff;

`;

const MobileFilterSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
`;

const MobileFilterIcon = styled.div`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  display: grid;
  place-items: center;
  color: #1e1e1e;
`;

const MobileCompanyLogoImage = styled.img`
  display: block;
  width: 48px;
  height: 48px;
  object-fit: cover;
`;

const MobileFilterCopy = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
`;

const MobileFilterTitle = styled.div`
  width: 100%;
  overflow: hidden;
  color: #1e1e1e;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileFilterAssetName = styled.div`
  width: 100%;
  overflow: hidden;
  color: #43536b;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileFilterRange = styled.div`
  width: 100%;
  overflow: hidden;
  color: #768499;
  font-size: 11px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobileFilterButton = styled.button`
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  color: #1e1e1e;
  border: 1px solid #e8ecf3;
  border-radius: 50%;
  background: #ffffff;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #2196f3;
    outline-offset: 2px;
  }
`;

const MobileFilterForm = styled.div`
  width: 100%;
`;

const MobileChartPanel = styled.div`
  @media (max-width: 767px) {
    margin: 18px 20px 0;
    overflow: visible;
    background: transparent;
    padding: 0 0 20px;
  }
`;

const FilterBottomSheetDrawer = styled(Drawer)`
  .ant-drawer-content {
    border-radius: 8px 8px 0 0;
    overflow: hidden;
  }

  .ant-drawer-header {
    padding: 16px 12px;
    border-bottom: 1px solid #e8ecf3;
  }

  .ant-drawer-header-title {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .ant-drawer-title {
    flex: 0 1 auto;
    color: #1e1e1e;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
  }

  .ant-drawer-close {
    width: 24px;
    height: 24px;
    margin-inline-end: 0;
    margin-inline-start: 0;
    padding: 0;
    color: #1e1e1e;
  }
`;

export default ReportLog;
